import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { searchNodes } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSignedIn } = useAuthStore();
  const [inputValue, setInputValue] = useState(searchParams.get("q") ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isSignedIn) void navigate("/", { replace: true });
  }, [isSignedIn, navigate]);

  // Debounce: update query param 300ms after input stops
  function handleInput(value: string) {
    setInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchParams(value ? { q: value } : {}, { replace: true });
    }, 300);
  }

  const query = searchParams.get("q") ?? "";

  const { data, isLoading } = useQuery({
    queryKey: ["search", query],
    queryFn: () => searchNodes(query, { limit: 50 }),
    enabled: isSignedIn && query.length >= 2,
  });

  if (!isSignedIn) return null;

  const hits = data?.items ?? [];

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Search</h1>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={inputValue}
          onChange={(e) => handleInput(e.target.value)}
          placeholder="Search pages across all sessions…"
          className="pl-9"
          autoFocus
        />
      </div>

      {query.length > 0 && query.length < 2 && (
        <p className="text-muted-foreground text-sm">
          Type at least 2 characters to search.
        </p>
      )}

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      )}

      {!isLoading && query.length >= 2 && hits.length === 0 && (
        <p className="text-muted-foreground text-sm">
          No results for &ldquo;{query}&rdquo;.
        </p>
      )}

      {hits.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground mb-3">
            {hits.length} result{hits.length !== 1 ? "s" : ""}
          </p>
          {hits.map((hit) => {
            const node = hit.node;
            if (!node) return null;
            const url = `/sessions/${hit.sessionId}?nodeId=${node.id}`;
            return (
              <Link
                key={`${hit.sessionId}-${node.id}`}
                to={url}
                className="block rounded-lg border p-4 hover:border-primary hover:bg-accent transition-colors"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="font-medium line-clamp-1">
                    {node.page?.title || node.canonicalUrl}
                  </span>
                  <Badge variant="outline" className="shrink-0 text-xs">
                    {hit.sessionName || "Session"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {node.canonicalUrl}
                </p>
              </Link>
            );
          })}

          {data?.hasMore && (
            <p className="text-center text-sm text-muted-foreground pt-2">
              Showing first 50 results. Refine your query for more specific
              results.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
